import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import type { TrendWidePoint, AIModel } from '@ekorank/shared'
import { MODEL_DISPLAY } from '@/lib/modelDisplay'

interface Props {
  data: TrendWidePoint[]
}

export function MentionTrendChart({ data }: Props) {
  if (data.length === 0) {
    return (
      <div className="bg-surface-container rounded-xl border border-outline-variant/10 p-4 sm:p-6 mb-6 sm:mb-10">
        <h3 className="text-sm font-mono text-outline uppercase tracking-widest mb-6">Mention Trend (30 days)</h3>
        <p className="text-on-surface-variant text-sm">No data yet — run a check to see your trend over time.</p>
      </div>
    )
  }

  // Discover which model keys actually appear in the data so we only render lines for them.
  const modelsInData = new Set<string>()
  for (const point of data) {
    for (const m of Object.keys(point.perModel)) modelsInData.add(m)
  }
  const orderedModels = Array.from(modelsInData) as AIModel[]

  // Pivot wide-format perModel object into flat row keys for Recharts dataKey access.
  const chartData = data.map((p) => ({
    date: p.date,
    ...p.perModel,
  }))

  return (
    <div className="bg-surface-container rounded-xl border border-outline-variant/10 p-4 sm:p-6 mb-6 sm:mb-10">
      <h3 className="text-sm font-mono text-outline uppercase tracking-widest mb-6">Mention Trend (30 days)</h3>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#353534" />
          <XAxis dataKey="date" tick={{ fill: '#908fa0', fontSize: 11 }} />
          <YAxis tick={{ fill: '#908fa0', fontSize: 11 }} allowDecimals={false} />
          <Tooltip
            contentStyle={{ background: '#201f1f', border: '1px solid #464554', borderRadius: 4 }}
            labelStyle={{ color: '#e5e2e1' }}
          />
          <Legend />
          {orderedModels.map((model) => (
            <Line
              key={model}
              type="monotone"
              dataKey={model}
              name={MODEL_DISPLAY[model]?.label ?? model}
              stroke={MODEL_DISPLAY[model]?.color ?? '#908fa0'}
              strokeWidth={2}
              dot={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
